
import React, { useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Media } from "@/components/media/types";

interface PhotoSlideshowProps {
  isOpen: boolean;
  onClose: () => void;
  photos: Media[];
  currentSlide: number;
  onPrevious: () => void;
  onNext: () => void;
}

export const PhotoSlideshow = ({
  isOpen,
  onClose,
  photos,
  currentSlide,
  onPrevious,
  onNext,
}: PhotoSlideshowProps) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Setup auto-advance timer
  useEffect(() => {
    if (isOpen && photos.length > 1) {
      // Start the timer when slideshow is open
      timerRef.current = setInterval(() => {
        onNext();
      }, 3000);
    }
    
    // Clean up the timer when component unmounts or slideshow closes
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isOpen, photos.length, onNext]);
  
  // Reset timer when manually changing slides
  const handleManualNext = () => {
    // Reset the timer when manually changing slides
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    onNext();
    
    // Restart the timer
    if (isOpen && photos.length > 1) {
      timerRef.current = setInterval(onNext, 3000);
    }
  };
  
  const handleManualPrevious = () => {
    // Reset the timer when manually changing slides
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    onPrevious();
    
    // Restart the timer
    if (isOpen && photos.length > 1) {
      timerRef.current = setInterval(onNext, 3000);
    }
  };

  if (!photos.length) return null;

  const currentPhoto = photos[currentSlide];
  const streamId = currentPhoto?.stream_id;
  const isVideo = streamId || /\.(mp4|mov|quicktime|m4v|avi)$/i.test(currentPhoto?.url);
  const fileExtension = currentPhoto?.url.split('.').pop()?.toLowerCase();

  const getVideoMimeType = (ext: string | undefined) => {
    switch (ext) {
      case 'mov':
      case 'quicktime':
        return 'video/quicktime';
      case 'm4v':
        return 'video/x-m4v';
      case 'avi':
        return 'video/x-msvideo';
      default:
        return 'video/mp4';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl h-[80vh] flex items-center justify-center p-0">
        <DialogTitle className="sr-only">Photo Slideshow</DialogTitle>
        <div className="relative w-full h-full flex items-center justify-center">
          {isVideo ? (
            streamId ? (
              <iframe
                src={`https://iframe.videodelivery.net/${streamId}`}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="w-full h-full border-0"
              />
            ) : (
              <video
                controls
                className="max-h-full max-w-full object-contain"
                playsInline
                poster={currentPhoto?.thumbnail_url || `/video-thumbnail-${(Math.abs(currentSlide % 3) + 1)}.jpg`}
              >
                <source src={currentPhoto?.url} type={getVideoMimeType(fileExtension)} />
                <source src={currentPhoto?.url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )
          ) : (
            <img
              src={currentPhoto?.url}
              alt={currentPhoto?.caption || "Slideshow image"}
              className="max-h-full max-w-full object-contain"
            />
          )}
          
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-2 top-1/2 transform -translate-y-1/2"
            onClick={handleManualPrevious}
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-1/2 transform -translate-y-1/2"
            onClick={handleManualNext}
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
